/**
 * clienteDoPush — decide qual cliente HTTP (axios) usar para uma chamada de
 * Web Push, a partir do `basePath` (Task 6c).
 *
 * Por que a decisão mora num lugar só: até aqui CADA hook de push decidia
 * sozinho qual cliente usar, comparando `basePath` por conta própria — e as
 * duas cópias da decisão DIVERGIRAM. `usePushSubscription.ts` (Task 6b) foi
 * corrigido para escolher `portalApi` quando `basePath === PUSH_BASE_PORTAL`;
 * `useServerHasVapid.ts` continuava batendo sempre no `api` interno,
 * ignorando o `basePath` que recebia. No Portal do Parceiro isso produzia
 * dois efeitos ruins ao mesmo tempo:
 *
 *   1. `GET /api/v1/portal/push/vapid-key` com o cliente interno (sem
 *      `seweb_auth`) voltava 401 → `useServerHasVapid` concluía "servidor
 *      sem VAPID" → o convite de notificação (gate em
 *      `lib/notificationPrompt.ts`: `serverHasVapid !== true` ⇒ não mostra)
 *      NUNCA aparecia para o parceiro, mesmo com o resto já corrigido.
 *   2. O interceptador do cliente interno reage a 401 apagando `seweb_auth`
 *      do `localStorage`: um parceiro com sessão interna aberta no mesmo
 *      navegador era deslogado do sistema interno só por abrir o portal.
 *
 * A partir de agora TODO hook de push (subscribe/unsubscribe, checagem de
 * VAPID, e qualquer um que vier depois) chama `clienteDoPush(basePath)` — só
 * existe um lugar onde a decisão pode estar errada, e as duas não podem
 * mais divergir.
 *
 * Refs: `V1/backend/app/api/v1/api.py` — `push.router` é montado 2×: uma
 * vez sem prefixo (`/api/v1/push/*`, sistema interno) e uma vez com
 * `prefix="/portal"` (`/api/v1/portal/push/*`, mesmo router — UI-4b).
 */
import type { AxiosInstance } from "axios";

import api from "@/services/api";
import { portalApi } from "@/services/portalApi";

/** Prefixo das rotas de push quando quem chama é o sistema interno. */
export const PUSH_BASE_INTERNO = "/push";

/**
 * Prefixo das rotas de push quando quem chama é o Portal do Parceiro.
 *
 * O parceiro do Portal NÃO alcança `/push/*`: o `PortalAccessMiddleware` é
 * DEFAULT DENY e só libera `/api/v1/portal/*`. As MESMAS rotas são montadas
 * sob `/portal` no backend (UI-4b), então o portal passa `"/portal/push"`
 * aqui. Um valor só, num lugar só.
 */
export const PUSH_BASE_PORTAL = "/portal/push";

/**
 * Qual cliente axios falar para um dado `basePath` de push.
 *
 * `PUSH_BASE_PORTAL` → o axios do portal (`services/portalApi.ts`, token +
 * renovação do parceiro). Qualquer outro valor (o padrão de ambos os hooks
 * é `PUSH_BASE_INTERNO`) → o `api` interno, como sempre.
 */
export function clienteDoPush(basePath: string): AxiosInstance {
  return basePath === PUSH_BASE_PORTAL ? portalApi : api;
}
